/* ============================================================================
   CONFIGURACIÓN DE CANASTA (Heli-Utility Basket) — CC-CSU.

   Fuente: "Enmienda de actualización peso y balance Nro. 03", Eagle Worldwide
   Fleet Management CMA N° 492, 18 de diciembre de 2024, firmada por el
   Ing. Aeronáutico Mauricio Torres Méndez, Lic. 214. AS350B3 S/N 4338.

   El documento suma dos STC al peso vacío anterior:

     Peso vacío anterior ............... 1360,80 kg · momento long. 4709,740
     STC 1  VHF-FM / sirena / Bambi ....    8,17 kg · momento long.   21,850
     STC 2  Heli-Utility Basket + tubo ..  38,80 kg · momento long.  133,084
     ─────────────────────────────────────────────────────────────────────
     NUEVO EMPTY WEIGHT & CG LONG ...... 1407,77 kg · momento long. 4864,674

   La aritmética longitudinal cierra exacta: 4864,674 / 1407,77 = 3,4556 m,
   que redondea al 3,46 m que declara el documento.

   ADVERTENCIA SOBRE EL BALANCE LATERAL
   ------------------------------------
   La tabla lateral del documento NO cierra: los renglones suman
   −17,14 + 0,31 − 48,50 = −65,33 kg·m, pero el total declarado es −31,05 kg·m
   (que sí corresponde al brazo −0,02 m que figura). Uno de los dos números
   está mal transcrito o mal leído en el escaneo.

   Acá se guarda el TOTAL declarado, no la suma de los renglones, porque el
   total es coherente con el brazo firmado. Queda marcado como pendiente de
   verificar contra el original en papel. El cálculo longitudinal —el que usa
   la envolvente de CG de la app— no depende de esto.
   ========================================================================== */

/** Peso y momento longitudinal del peso vacío CON la canasta instalada. */
export const PESO_VACIO_CON_CANASTA = {
  pesoKg: 1407.77,
  momentoLongKgM: 4864.674,
  momentoLatKgM: -31.05,
  fuente: "Enmienda de peso y balance Nro. 03 — 18/12/2024",
  lateralVerificado: false,
};

/**
 * Canasta y tubo de carga larga, según el STC FAA SR00213NY.
 *
 * `brazoCargaM` es el brazo de la INSTALACIÓN, no el de la carga que se pone
 * adentro. El suplemento del RFM del STC da el brazo del compartimento; no lo
 * tengo. Se usa el de la instalación como mejor aproximación disponible y
 * queda marcado para verificar: una carga de 120 kg con el brazo equivocado
 * mueve el CG lo suficiente como para importar.
 */
export const CANASTA_STC = {
  designacion: "FAA STC SR00213NY — Heli-Utility Basket and Long Cargo Tube",
  pesoKg: 38.8,
  momentoLongKgM: 133.084,
  momentoLatKgM: -48.5,
  capacidadMaxKg: 120,
  brazoCargaM: 133.084 / 38.8,
  brazoCargaVerificado: false,
};

/**
 * Peso vacío y brazo efectivos según la canasta esté instalada o no.
 *
 * Quitar la canasta no es restar 38,8 kg y dejar el brazo igual: hay que
 * restar también su momento y volver a dividir. Da un brazo distinto — poco,
 * porque la canasta va casi sobre el CG, pero distinto.
 *
 * @param {boolean} instalada
 * @param {{pesoKg:number, momentoLongKgM:number}} base peso vacío CON canasta
 * @param {{pesoKg:number, momentoLongKgM:number}} canasta
 */
export function pesoVacioSegunConfiguracion(instalada, base = PESO_VACIO_CON_CANASTA, canasta = CANASTA_STC) {
  const valido = Number.isFinite(base?.pesoKg) && Number.isFinite(base?.momentoLongKgM)
    && Number.isFinite(canasta?.pesoKg) && Number.isFinite(canasta?.momentoLongKgM);
  if (!valido) {
    return { pesoKg: null, brazoM: null, momentoKgM: null, configuracion: null, valido: false };
  }

  const pesoKg = instalada ? base.pesoKg : base.pesoKg - canasta.pesoKg;
  const momentoKgM = instalada ? base.momentoLongKgM : base.momentoLongKgM - canasta.momentoLongKgM;

  if (pesoKg <= 0) {
    return { pesoKg: null, brazoM: null, momentoKgM: null, configuracion: null, valido: false };
  }

  return {
    valido: true,
    configuracion: instalada ? "con canasta" : "sin canasta",
    pesoKg: Math.round(pesoKg * 100) / 100,
    momentoKgM: Math.round(momentoKgM * 1000) / 1000,
    brazoM: Math.round((momentoKgM / pesoKg) * 10000) / 10000,
  };
}

/**
 * Comprueba la carga de la canasta contra su límite.
 *
 * Sin canasta instalada, cualquier carga en ella es un error de carga, no un
 * sobrepeso: se rechaza entera, no se compara contra 120 kg.
 */
export function verificarCargaCanasta(kg, instalada, capacidadMaxKg = CANASTA_STC.capacidadMaxKg) {
  const carga = Number.isFinite(kg) ? kg : 0;
  if (!instalada) {
    return {
      admisible: carga === 0,
      excesoKg: carga,
      mensaje: carga > 0
        ? "Hay carga asignada a la canasta pero la configuración es SIN canasta. O se instala, o se quita la carga."
        : null,
    };
  }
  if (carga > capacidadMaxKg) {
    return {
      admisible: false,
      excesoKg: Math.round((carga - capacidadMaxKg) * 10) / 10,
      mensaje: `La canasta admite ${capacidadMaxKg} kg y lleva ${carga} kg: ${Math.round(carga - capacidadMaxKg)} kg de más.`,
    };
  }
  return { admisible: true, excesoKg: 0, mensaje: null };
}
